<template>
  <div class="home">
    <div class="kpi-row" :style="{ gridTemplateColumns: 'repeat(' + kpiList.length + ', 1fr)' }">
      <div
        v-for="item in kpiList"
        :key="item.key"
        class="kpi"
        :class="{ 'kpi--ring': item.hasRing }"
      >
        <div class="kpi__icon-wrap">
          <i :class="item.icon" class="kpi__icon" aria-hidden="true" />
        </div>
        <div class="kpi__body">
          <div class="kpi__label">{{ item.label }}</div>
          <div class="kpi__value">
            <span class="num">{{ item.value }}</span>
            <span class="unit">{{ item.unit }}</span>
          </div>
        </div>
        <div v-if="item.hasRing" class="kpi__ring">
          <svg viewBox="0 0 36 36">
            <path
              class="bg"
              d="M18 2.5 a 15.5 15.5 0 1 1 0 31 a 15.5 15.5 0 1 1 0 -31"
            />
            <path
              class="fg"
              :stroke-dasharray="item.ring + ',100'"
              d="M18 2.5 a 15.5 15.5 0 1 1 0 31 a 15.5 15.5 0 1 1 0 -31"
            />
          </svg>
        </div>
      </div>
    </div>

    <div class="mid">
      <section class="col left">
        <div class="panel">
          <h3>新阶层五类人员占比</h3>
          <div class="pie-wrap">
            <div ref="pie" class="chart chart--pie"></div>
            <div class="pie-center">
              <div>合计</div>
              <strong>{{ stratumTotal }}人</strong>
            </div>
          </div>
        </div>
        <div class="panel">
          <h3>近12月新会员增长</h3>
          <div ref="line" class="chart"></div>
        </div>
      </section>

      <section class="col center">
        <div class="panel map-panel">
          <div class="news" v-if="!inspectMode">
            <div class="news__head">
              <h4>工作动态</h4>
              <button type="button" class="news__more" @click="openNews">更多 &gt;</button>
            </div>
            <div
              v-if="news.length"
              class="news__viewport"
              @mouseenter="pauseNewsSwiper"
              @mouseleave="resumeNewsSwiper"
            >
              <swiper
                :key="newsSwiperKey"
                ref="newsSwiper"
                class="news__swiper"
                :options="newsSwiperOption"
              >
                <swiper-slide v-for="(n, i) in news" :key="i">
                  <div class="news__item" @click="openNewsItem(n)">
                    <i :class="['news__icon', newsIcon(n.category)]" />
                    <span class="c">{{ n.title }}</span>
                    <span class="t">{{ n.time }}</span>
                  </div>
                </swiper-slide>
              </swiper>
            </div>
            <p v-else class="news__empty">暂无动态</p>
          </div>
          <div class="map">
            <dash-geo-map
              :map-nodes="mapNodes"
              :common-font="commonFont"
              map-mode="shibei"
              @node-click="openBranchMapPopup"
            />
            <div
              v-if="branchPopup.open"
              class="branch-popup"
            >
              <transition
                appear
                enter-active-class="animate__animated animate__zoomIn"
                leave-active-class="animate__animated animate__zoomOut"
                @after-leave="onBranchPopupAfterLeave"
              >
                <div
                  v-if="branchPopup.visible"
                  key="branch-popup-panel"
                  class="branch-popup__panel"
                  @click.stop
                >
                  <button type="button" class="branch-popup__close" @click="closeBranchMapPopup">
                    ×
                  </button>
                  <h4 class="branch-popup__title">{{ branchPopup.name }}</h4>
                  <div class="branch-popup__kpis">
                    <div>
                      <span>会员</span>
                      <em>{{ branchPopup.memberCount }}</em>
                    </div>
                    <div>
                      <span>活动合计</span>
                      <em>{{ branchPopup.activityCount }}</em>
                    </div>
                  </div>
                  <p class="branch-popup__sub">年度五类活动</p>
                  <ul class="branch-popup__types">
                    <li v-for="t in branchPopup.activityTypes" :key="t.name">
                      <span>{{ t.name }}</span>
                      <em>{{ t.value || 0 }} <small>场</small></em>
                    </li>
                  </ul>
                </div>
              </transition>
            </div>
          </div>
          <div class="honor">
            <div class="honor__head">
              <h4>荣誉 / 活动 / 品牌作品</h4>
            </div>
            <p v-if="!honorSlides.length" class="honor__empty">暂无轮播内容（后台：轮播管理，须选分会并上屏）</p>
            <div
              v-else
              class="honor__viewport"
              @mouseenter="honorPaused = true"
              @mouseleave="honorPaused = false"
            >
              <div
                class="honor__track"
                :style="honorTrackStyle"
              >
                <button
                  v-for="(c, i) in honorLoopSlides"
                  :key="i"
                  type="button"
                  class="honor__card"
                  @click="openHonorLightbox(c._idx)"
                >
                  <div
                    class="honor__ph"
                    :style="honorPhStyle(c)"
                  />
                  <div class="honor__cap">
                    <span>{{ c.title }}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="lightbox.visible"
            class="lightbox"
            @click.self="closeHonorLightbox"
          >
            <button type="button" class="lightbox__close" @click="closeHonorLightbox">
              ×
            </button>
            <button
              type="button"
              class="lightbox__nav lightbox__nav--prev"
              @click.stop="lightboxStep(-1)"
            >
              ‹
            </button>
            <div class="lightbox__stage" @click.stop>
              <img
                v-if="lightboxItem"
                :src="lightboxItem.cover"
                :alt="lightboxItem.title"
                class="lightbox__img"
                @touchstart.passive="onLightboxTouchStart"
                @touchend.passive="onLightboxTouchEnd"
              />
              <div v-if="lightboxItem" class="lightbox__meta">
                <em v-if="lightboxItem.category">{{ lightboxItem.category }}</em>
                <strong>{{ lightboxItem.title }}</strong>
                <span>{{ lightbox.index + 1 }} / {{ honorSlides.length }}</span>
              </div>
            </div>
            <button
              type="button"
              class="lightbox__nav lightbox__nav--next"
              @click.stop="lightboxStep(1)"
            >
              ›
            </button>
          </div>
        </div>
      </section>

      <section class="col right">
        <div class="panel">
          <h3>活动类型分布</h3>
          <div ref="bar" class="chart chart--bar"></div>
        </div>
        <div class="panel vol">
          <h3>公益服务数据</h3>
          <div class="vol-grid">
            <div v-for="v in volCards" :key="v.label">
              <i :class="v.icon" />
              <span>{{ v.label }}</span>
              <em>{{ v.value }} <small>{{ v.unit }}</small></em>
            </div>
          </div>
        </div>
        <div class="panel brand">
          <div
            class="brand__cover"
            :class="{ 'has-image': !!(brand && brand.coverUrl) }"
            :style="brandCoverStyle"
          />
          <button type="button" class="brand__more" @click="openBrand">更多</button>
          <div class="brand__body">
            <h3>{{ brand.name || "寻美青岛" }}进行中</h3>
            <p class="brand__label">{{ brand.metricLabel || "主指标" }}</p>
            <div class="brand__num">
              <b>{{ brand.workCount || 0 }}</b>
              <span>件</span>
            </div>
            <button type="button" class="brand__btn" @click="openBrand">
              立即查看 &gt;
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { PieChart, LineChart, BarChart } from "@/plugins/echarts_bank/index";
import DashGeoMap from "@/components/dash/DashGeoMap.vue";
import "remixicon/fonts/remixicon.css";
import { absMediaUrl, toDetailItem } from "@/utils/mediaUrl";
import { fetchBrandMaterialDrawer } from "@/utils/brandMaterials";
import branchQuery from "@/utils/branchQuery";
import { isSameJson } from "@/utils/stableData";

export default {
  name: "DashOverview",
  components: { DashGeoMap },
  mixins: [branchQuery],
  props: {
    rem: { type: Number, required: true },
    commonFont: { type: Number, required: true },
    home: { type: Object, default: () => ({}) },
    inspectMode: { type: Boolean, default: false }
  },
  data() {
    return {
      pie: null,
      line: null,
      bar: null,
      displayKpi: { m: 0, a: 0, p: 0, v: 0, s: 0, r: 0 },
      kpiRaf: null,
      kpiSig: "",
      chartSig: null,
      honorSig: null,
      honorPaused: false,
      honorIndex: 0,
      honorTimer: null,
      lightbox: { visible: false, index: 0 },
      lightboxTouchX: null,
      branchPopup: {
        open: false,
        visible: false,
        name: "",
        memberCount: 0,
        activityCount: 0,
        activityTypes: []
      }
    };
  },
  computed: {
    kpis() {
      return (this.home && this.home.kpis) || {};
    },
    stratumList() {
      return (this.home && this.home.stratum) || [];
    },
    stratumTotal() {
      return this.stratumList.reduce((sum, x) => sum + (Number(x.value) || 0), 0);
    },
    kpiTargets() {
      const k = this.kpis;
      return {
        m: Number(k.memberCount) || 0,
        a: Number(k.activityCount) || 0,
        p: Number(k.participantCount) || 0,
        v: Number(k.volunteerHours) || 0,
        s: Number(k.servedCount) || 0,
        r: Number(k.suggestionRate) || 0
      };
    },
    kpiList() {
      const d = this.displayKpi;
      const list = [
        { key: "m", label: "会员总数", value: Math.round(d.m), unit: "人", icon: "ri-group-fill" },
        { key: "a", label: "年度活动", value: Math.round(d.a), unit: "场", icon: "ri-calendar-event-fill" },
        { key: "p", label: "参与人次", value: Math.round(d.p), unit: "次", icon: "ri-user-add-fill" },
        { key: "v", label: "公益时长", value: Math.round(d.v), unit: "小时", icon: "ri-timer-fill" },
        { key: "s", label: "服务群众", value: Math.round(d.s), unit: "人", icon: "ri-heart-fill" }
      ];
      // 迎检模式下隐藏办结率指标
      if (!this.inspectMode) {
        list.push({
          key: "r",
          label: "建言办结率",
          value: Math.round(d.r),
          unit: "%",
          hasRing: true,
          ring: Math.min(100, Math.max(0, d.r)),
          icon: "ri-pie-chart-fill"
        });
      }
      return list;
    },
    news() {
      return (this.home && this.home.news) || [];
    },
    /** 一屏 4 条；多于 4 条才纵向无缝轮播，每次滚 1 条 */
    newsCanLoop() {
      return this.news.length > 4;
    },
    newsSwiperKey() {
      const list = this.news;
      const head = list
        .slice(0, 3)
        .map(n => `${n.title || ""}|${n.time || ""}`)
        .join(";");
      return `news-${list.length}-${head}`;
    },
    newsSwiperOption() {
      const canLoop = this.newsCanLoop;
      return {
        direction: "vertical",
        slidesPerView: 4,
        spaceBetween: 0,
        loop: canLoop,
        loopedSlides: canLoop ? this.news.length : undefined,
        speed: 500,
        autoplay: canLoop
          ? { delay: 2000, disableOnInteraction: false }
          : false,
        allowTouchMove: true,
        observer: true,
        observeParents: true
      };
    },
    carousel() {
      return (this.home && this.home.carousel) || [];
    },
    honorSlides() {
      const slides = [];
      (this.carousel || []).forEach(c => {
        const urls = [];
        const add = u => {
          const url = String(u || "").trim();
          if (!url || urls.indexOf(url) >= 0) return;
          urls.push(url);
        };
        // 优先用接口汇总的 galleryUrls（含封面+图集）
        const gallery = c.galleryUrls || c.GalleryUrls || [];
        if (Array.isArray(gallery) && gallery.length) {
          gallery.forEach(add);
        } else {
          add(c.coverUrl || c.CoverUrl);
          const raw = c.galleryText || c.GalleryText || "";
          if (raw) {
            String(raw)
              .split(/[\n,;|，；]+/)
              .forEach(add);
          }
        }
        const title = c.title || "作品";
        const category = c.category || "";
        if (!urls.length) {
          slides.push({
            _idx: slides.length,
            title,
            category,
            cover: ""
          });
          return;
        }
        urls.forEach(url => {
          slides.push({
            _idx: slides.length,
            title,
            category,
            cover: this.absCover(url)
          });
        });
      });
      return slides;
    },
    honorLoopSlides() {
      const list = this.honorSlides;
      // 满 4 张才做无缝轮播（复制一份）；不足 4 张静态展示
      if (list.length < 4) return list;
      return list.concat(list.map((c, i) => ({ ...c, _idx: i })));
    },
    honorTrackStyle() {
      const n = this.honorSlides.length;
      // 不足 4 张不位移
      if (n < 4) {
        return { transform: "translateX(0)", transition: "none" };
      }
      // 视口正好 4 张，每次横移一张（25%）
      const step = 25;
      return {
        transform: `translateX(-${this.honorIndex * step}%)`,
        transition: this.honorIndex === 0 ? "none" : "transform 0.55s ease"
      };
    },
    lightboxItem() {
      return this.honorSlides[this.lightbox.index] || null;
    },
    mapNodes() {
      return (this.home && this.home.mapNodes) || [];
    },
    volunteer() {
      return (this.home && this.home.volunteer) || {};
    },
    volCards() {
      const v = this.volunteer;
      return [
        { label: "公益场次", value: v.sessions || 0, unit: "场", icon: "el-icon-s-flag" },
        { label: "志愿者", value: v.people || 0, unit: "人", icon: "el-icon-user" },
        { label: "服务群众", value: v.served || 0, unit: "人", icon: "el-icon-s-custom" }
      ];
    },
    brand() {
      return (this.home && this.home.brand) || {};
    },
    brandCoverStyle() {
      const url = this.brand && this.brand.coverUrl;
      if (!url) return { backgroundImage: "none", backgroundColor: "transparent" };
      return { backgroundImage: `url(${this.absCover(url)})` };
    }
  },
  watch: {
    home: {
      deep: true,
      handler(val) {
        // KPI 自身有签名；图/荣誉仅在对应切片变化时刷新，避免 10s 空转整页动画
        this.syncKpiRoll();
        const nextChart = {
          stratum: (val && val.stratum) || [],
          growth: (val && val.growth) || [],
          activityTypes: (val && val.activityTypes) || []
        };
        if (!isSameJson(nextChart, this.chartSig)) {
          this.chartSig = nextChart;
          this.renderCharts();
        }
        const nextHonor = (val && val.carousel) || [];
        if (!isSameJson(nextHonor, this.honorSig)) {
          this.honorSig = nextHonor;
          this.startHonorAutoplay();
        }
      }
    },
    commonFont() {
      this.$nextTick(() => {
        this.destroyCharts();
        this.initCharts();
        this.chartSig = null;
        this.renderCharts();
        this.chartSig = {
          stratum: this.stratumList || [],
          growth: (this.home && this.home.growth) || [],
          activityTypes: (this.home && this.home.activityTypes) || []
        };
      });
    }
  },
  mounted() {
    this.initCharts();
    this.syncKpiRoll(true);
    this.chartSig = {
      stratum: this.stratumList || [],
      growth: (this.home && this.home.growth) || [],
      activityTypes: (this.home && this.home.activityTypes) || []
    };
    this.honorSig = (this.home && this.home.carousel) || [];
    this.renderCharts();
    this.startHonorAutoplay();
    window.addEventListener("keydown", this.onLightboxKeydown);
  },
  beforeDestroy() {
    this.cancelKpiRoll();
    this.destroyCharts();
    this.stopHonorAutoplay();
    window.removeEventListener("keydown", this.onLightboxKeydown);
  },
  methods: {
    openBranchMapPopup(node) {
      if (!node) return;
      const types = Array.isArray(node.activityTypes) ? node.activityTypes : [];
      this.branchPopup = {
        open: true,
        visible: true,
        name: node.name || "分会",
        memberCount: Number(node.memberCount) || 0,
        activityCount: Number(node.activityCount) || 0,
        activityTypes: types.map(t => ({
          name: (t && t.name) || "",
          value: Number(t && t.value) || 0
        }))
      };
    },
    closeBranchMapPopup() {
      if (!this.branchPopup.visible) return;
      this.branchPopup.visible = false;
    },
    onBranchPopupAfterLeave() {
      this.branchPopup = {
        open: false,
        visible: false,
        name: "",
        memberCount: 0,
        activityCount: 0,
        activityTypes: []
      };
    },
    startHonorAutoplay() {
      this.stopHonorAutoplay();
      this.honorIndex = 0;
      // 视口固定 4 格：不足 4 张不自动轮播
      if (this.honorSlides.length < 4) return;
      this.honorTimer = setInterval(() => {
        if (this.honorPaused || this.lightbox.visible) return;
        const n = this.honorSlides.length;
        if (n < 4) return;
        const next = this.honorIndex + 1;
        this.honorIndex = next;
        if (next >= n) {
          setTimeout(() => {
            if (this.honorIndex >= n) this.honorIndex = 0;
          }, 560);
        }
      }, 3000);
    },
    stopHonorAutoplay() {
      if (this.honorTimer) {
        clearInterval(this.honorTimer);
        this.honorTimer = null;
      }
    },
    openHonorLightbox(index) {
      const n = this.honorSlides.length;
      if (!n) return;
      this.lightbox = {
        visible: true,
        index: ((index % n) + n) % n
      };
    },
    closeHonorLightbox() {
      this.lightbox = { visible: false, index: 0 };
    },
    lightboxStep(delta) {
      const n = this.honorSlides.length;
      if (!n) return;
      this.lightbox.index = (this.lightbox.index + delta + n) % n;
    },
    onLightboxKeydown(e) {
      if (this.branchPopup.open && e.key === "Escape") {
        this.closeBranchMapPopup();
        return;
      }
      if (!this.lightbox.visible) return;
      if (e.key === "Escape") this.closeHonorLightbox();
      if (e.key === "ArrowLeft") this.lightboxStep(-1);
      if (e.key === "ArrowRight") this.lightboxStep(1);
    },
    onLightboxTouchStart(e) {
      const t = e.changedTouches && e.changedTouches[0];
      this.lightboxTouchX = t ? t.clientX : null;
    },
    onLightboxTouchEnd(e) {
      const t = e.changedTouches && e.changedTouches[0];
      if (this.lightboxTouchX == null || !t) return;
      const dx = t.clientX - this.lightboxTouchX;
      this.lightboxTouchX = null;
      if (Math.abs(dx) < 40) return;
      this.lightboxStep(dx < 0 ? 1 : -1);
    },
    cancelKpiRoll() {
      if (this.kpiRaf) {
        cancelAnimationFrame(this.kpiRaf);
        this.kpiRaf = null;
      }
    },
    syncKpiRoll(forceFromZero) {
      const targets = this.kpiTargets;
      const sig = JSON.stringify(targets);
      if (!forceFromZero && sig === this.kpiSig) return;
      const fromZero = forceFromZero || !this.kpiSig;
      this.kpiSig = sig;
      this.rollKpis(targets, fromZero);
    },
    rollKpis(targets, fromZero) {
      this.cancelKpiRoll();
      const keys = ["m", "a", "p", "v", "s", "r"];
      const start = {};
      keys.forEach(k => {
        start[k] = fromZero ? 0 : Number(this.displayKpi[k]) || 0;
      });
      const duration = 1000;
      const t0 = performance.now();
      const tick = now => {
        const p = Math.min(1, (now - t0) / duration);
        const e = 1 - Math.pow(1 - p, 3);
        const next = {};
        keys.forEach(k => {
          next[k] = start[k] + (targets[k] - start[k]) * e;
        });
        this.displayKpi = next;
        if (p < 1) {
          this.kpiRaf = requestAnimationFrame(tick);
        } else {
          this.displayKpi = { ...targets };
          this.kpiRaf = null;
        }
      };
      this.kpiRaf = requestAnimationFrame(tick);
    },
    destroyCharts() {
      if (this.pie) this.pie.destroy();
      if (this.line) this.line.destroy();
      if (this.bar) this.bar.destroy();
      this.pie = this.line = this.bar = null;
    },
    initCharts() {
      const fs = this.commonFont;
      const self = this;
      this.pie = new PieChart({
        ele: this.$refs.pie,
        fontSize: fs,
        palletColors: ["#3d8bff", "#2ec7c9", "#f5a623", "#9b7bff", "#8a9bb5"],
        legend: {
          show: true,
          orient: "vertical",
          left: "58%",
          right: "auto",
          top: "middle",
          align: "left",
          itemGap: Math.max(14, fs + 2),
          itemWidth: fs,
          itemHeight: fs,
          textStyle: { color: "#cfe3ff", fontSize: Math.max(11, fs - 1) },
          formatter(name) {
            const row = self.stratumList.find(x => x.name === name);
            if (!row) return name;
            const total = self.stratumTotal || 1;
            const pct = Math.round((100 * (Number(row.value) || 0)) / total);
            const short =
              name.length > 8 ? name.slice(0, 7) + "…" : name;
            return `${short}  ${pct}%`;
          }
        },
        tooltip: {
          show: true,
          trigger: "item",
          confine: true,
          formatter(res) {
            const pct = Math.round(res.percent || 0);
            return `${res.name}：${res.value} 人，占比 ${pct}%`;
          }
        },
        series: {
          name: "占比",
          center: ["30%", "50%"],
          radius: ["46%", "72%"],
          label: { show: false },
          labelLine: { show: false },
          labelDecimal: 0,
          emphasis: {
            scale: true,
            scaleSize: 6
          }
        }
      });
      this.line = new LineChart({
        ele: this.$refs.line,
        fontSize: fs,
        palletColors: [["#00c3ff", "#4c7dff"]],
        grid: {
          top: 2.4 * fs,
          right: 1.2 * fs,
          bottom: 2.2 * fs,
          left: 2.8 * fs
        },
        series: [
          {
            name: "新增",
            smooth: true,
            showSymbol: true,
            symbolSize: 8,
            label: {
              show: true,
              position: "top",
              distance: 6,
              fontSize: Math.max(10, fs - 2),
              color: "#7dffc8",
              formatter(p) {
                const rates = self._growthRates || [];
                const r = rates[p.dataIndex];
                if (r == null || Number.isNaN(r)) return "";
                if (!Number.isFinite(r)) return "—";
                const sign = r > 0 ? "+" : "";
                return `${sign}${r}%`;
              }
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "rgba(0,195,255,0.45)" },
                  { offset: 1, color: "rgba(0,195,255,0.02)" }
                ]
              }
            }
          }
        ]
      });
      this.bar = new BarChart({
        ele: this.$refs.bar,
        fontSize: fs,
        palletColors: [["#7ae7ff", "#1a6dff"]],
        mAxis: "xAxis",
        vAxis: "yAxis",
        grid: { top: 2.2 * fs, right: fs, bottom: 0.5 * fs, left: 0.5 * fs },
        series: [
          {
            type: "bar",
            name: "场次",
            barWidth: "42%",
            label: {
              show: true,
              position: "top",
              fontSize: fs,
              color: "#e8f4ff"
            }
          }
        ]
      });
    },
    bindPieEvents() {
      const chart = this.pie && this.pie._chart;
      if (!chart) return;
      chart.off("click");
      chart.on("click", params => {
        if (!params || params.componentType !== "series") return;
        if (!params.name || params.name === "暂无") return;
        this.openStratumDetail(params.name, params.value, params.percent);
      });
    },
    async openStratumDetail(name, value, percent) {
      const pct = Math.round(percent || 0);
      const pageSize = 20;
      const mapItem = m =>
        toDetailItem({
          title: m.name,
          content: [m.title, m.unit].filter(Boolean).join(" · ") || "会员",
          avatarUrl: m.avatarUrl,
          meta: m.isRepresentative ? "代表人士" : ""
        });
      const fetchPage = async (skip, take) => {
        const res = await this.$api("screen", "stratumMembers", {
          name,
          skip,
          take,
          ...this.branchQuery
        });
        if (!res || res.code !== 0) {
          return { items: [], hasMore: false, total: value, reps: 0 };
        }
        return {
          items: (res.items || []).map(mapItem),
          hasMore: !!res.hasMore,
          total: res.total != null ? res.total : value,
          reps: res.reps || 0
        };
      };
      let first;
      try {
        first = await fetchPage(0, pageSize);
      } catch (e) {
        first = { items: [], hasMore: false, total: value, reps: 0 };
      }
      this.$root.$emit("dash-drawer", {
        title: `${name} · 会员明细`,
        summary: [
          `本类人数：${first.total} 人`,
          `占比：${pct}%`,
          `代表人士：${first.reps} 人`
        ],
        items: first.items.length
          ? first.items
          : [
              toDetailItem({
                title: "暂无会员明细",
                content: "请在会员管理中维护新阶层类别"
              })
            ],
        hasMore: first.items.length ? first.hasMore : false,
        infinite: first.items.length
          ? {
              pageSize,
              fetch: fetchPage
            }
          : null
      });
    },
    async openBrand() {
      const b = this.brand || {};
      const payload = await fetchBrandMaterialDrawer(
        this.$api.bind(this),
        {
          name: b.name || "寻美青岛",
          key: b.key,
          status: b.status,
          content: b.content,
          workCount: b.workCount,
          coverUrl: b.coverUrl
        },
        this.branchQuery
      );
      this.$root.$emit("dash-drawer", payload);
    },
    openNews() {
      const items = this.news.map(toDetailItem).filter(Boolean);
      this.$root.$emit("dash-drawer", {
        title: "工作动态",
        items,
        list: this.news.map(n => `${n.time || ""}  ${n.title || ""}`.trim())
      });
    },
    openNewsItem(n) {
      if (!n) return;
      const item = toDetailItem(n);
      this.$root.$emit("dash-drawer", {
        title: "工作动态",
        items: item ? [item] : [],
        list: [`${n.time || ""}  [${n.category || "活动"}]  ${n.title || ""}`.trim()]
      });
    },
    pauseNewsSwiper() {
      const s = this.$refs.newsSwiper && this.$refs.newsSwiper.swiper;
      if (s && s.autoplay) s.autoplay.stop();
    },
    resumeNewsSwiper() {
      if (!this.newsCanLoop) return;
      const s = this.$refs.newsSwiper && this.$refs.newsSwiper.swiper;
      if (s && s.autoplay) s.autoplay.start();
    },
    newsIcon(category) {
      const c = (category || "").trim();
      if (c === "品牌活动") return "ri-star-fill";
      if (c === "学习教育") return "ri-book-open-fill";
      if (c === "会议培训") return "ri-calendar-event-fill";
      if (c === "公益服务") return "ri-heart-fill";
      if (c === "三单服务") return "ri-customer-service-2-fill";
      return "ri-calendar-event-fill";
    },
    absCover(url) {
      return absMediaUrl(url);
    },
    honorPhStyle(c) {
      const url = c && c.cover;
      if (!url) return {};
      return { backgroundImage: `url("${url}")` };
    },
    renderCharts() {
      if (!this.pie || !this.line || !this.bar) return;
      const stratum = this.stratumList;
      const growth = (this.home && this.home.growth) || [];
      const types = (this.home && this.home.activityTypes) || [];
      this.pie
        .setData(
          stratum.length
            ? stratum.map(x => ({ name: x.name, value: x.value }))
            : [{ name: "暂无", value: 1 }]
        )
        .draw();
      this.bindPieEvents();
      const growthPoints = growth.length
        ? growth.map(x => ({ name: x.name, value: Number(x.value) || 0 }))
        : [{ name: "—", value: 0 }];
      this._growthRates = growthPoints.map((p, i) => {
        if (i === 0) return null;
        const prev = growthPoints[i - 1].value;
        const cur = p.value;
        if (prev === 0) return cur === 0 ? 0 : null;
        return Math.round(((cur - prev) / prev) * 100);
      });
      this.line.setData([growthPoints]).draw();
      this.bar
        .setData([
          types.length
            ? types.map(x => ({ name: x.name, value: x.value }))
            : [
                { name: "学习教育", value: 0 },
                { name: "会议培训", value: 0 },
                { name: "公益服务", value: 0 },
                { name: "三单服务", value: 0 },
                { name: "品牌活动", value: 0 }
              ]
        ])
        .draw();
      this.bindBarEvents();
    },
    bindBarEvents() {
      const chart = this.bar && this.bar._chart;
      if (!chart) return;
      chart.off("click");
      chart.on("click", params => {
        if (!params || params.componentType !== "series") return;
        if (!params.name) return;
        this.openActivityTypeDetail(params.name, params.value);
      });
    },
    async openActivityTypeDetail(name, sessionsHint) {
      let sessions = Number(sessionsHint) || 0;
      let participants = 0;
      let items = [];
      const isSandan = name === "三单服务";
      const peopleLabel = isSandan ? "服务人数" : "参与人次";
      const peopleTag = n => (isSandan ? `服务 ${n} 人` : `参与 ${n} 人`);
      try {
        const res = await this.$api("screen", "activitiesByType", {
          name,
          ...this.branchQuery
        });
        if (res && res.code === 0) {
          sessions = res.sessions != null ? res.sessions : sessions;
          participants = res.participants || 0;
          items = (res.items || []).map(a =>
            toDetailItem({
              title: a.title,
              content: a.content || `${peopleLabel}：${a.participants || 0}`,
              coverUrl: a.coverUrl,
              time: a.time,
              category: a.category || name,
              status: a.participants != null ? peopleTag(a.participants) : a.status
            })
          );
        }
      } catch (e) {
        /* ignore */
      }
      this.$root.$emit("dash-drawer", {
        title: `${name} · 活动列表`,
        summary: [`场次：${sessions} 场`, `${peopleLabel}：${participants} 人`],
        items: items.length
          ? items
          : [
              toDetailItem({
                title: "暂无该类活动",
                content: "请在对应台账录入：学习教育 / 会议培训 / 公益服务 / 三单服务 / 品牌活动"
              })
            ]
      });
    }
  }
};
</script>

<style lang="less" scoped>
@import "~@/assets/less/dash-theme.less";

.home {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10 / @rem2;
}
.kpi-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10 / @rem2;
  flex-shrink: 0;
}
.kpi {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12 / @rem2;
  height: 78 / @rem2;
  min-height: 78 / @rem2;
  padding: 0 16 / @rem2 0 14 / @rem2;
  overflow: visible;
  .kpi-tech();
  &.kpi--ring {
    padding-right: 54 / @rem2;
  }
}
.kpi__icon-wrap {
  position: relative;
  z-index: 1;
  width: 40 / @rem2;
  height: 40 / @rem2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &::before {
    content: "";
    position: absolute;
    inset: 2 / @rem2;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      fade(@mainColor, 35%) 0%,
      fade(@mainColor, 8%) 48%,
      transparent 72%
    );
    pointer-events: none;
  }
}
.kpi__icon {
  position: relative;
  z-index: 1;
  font-size: 26 / @rem2;
  line-height: 1;
  color: #7ae7ff;
  filter: drop-shadow(0 0 4px fade(@mainColor, 70%));
}
.kpi__body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
}
.kpi__label {
  font-size: 13 / @rem2;
  font-weight: 400;
  color: #e8f4ff;
  letter-spacing: 0.5 / @rem2;
  line-height: 1.2;
}
.kpi__value {
  margin-top: 4 / @rem2;
  display: flex;
  align-items: baseline;
  .num {
    font-size: 30 / @rem2;
    font-weight: 700;
    color: #fff;
    line-height: 1;
  }
  .unit {
    margin-left: 4 / @rem2;
    font-size: 13 / @rem2;
    font-weight: 400;
    color: #cfe6f8;
  }
}
.kpi__ring {
  position: absolute;
  z-index: 1;
  right: 12 / @rem2;
  top: 50%;
  transform: translateY(-50%);
  width: 36 / @rem2;
  height: 36 / @rem2;
  svg { width: 100%; height: 100%; transform: rotate(-90deg); }
  .bg { fill: none; stroke: rgba(255,255,255,0.12); stroke-width: 3; }
  .fg { fill: none; stroke: @mainColor; stroke-width: 3; stroke-linecap: round; }
}
.mid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1.05fr 1.35fr 1.05fr;
  gap: 10 / @rem2;
}
.col {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10 / @rem2;
}
.panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 10 / @rem2 12 / @rem2;
  .panel-tech();
  h3 {
    .panel-title-bar();
    flex-shrink: 0;
  }
}
.chart {
  flex: 1;
  min-height: 140 / @rem2;
}
.chart--bar { min-height: 160 / @rem2; }
.chart--pie {
  width: 100%;
  height: 100%;
  min-height: 210 / @rem2;
  cursor: pointer;
}
.pie-wrap {
  position: relative;
  flex: 1;
  min-height: 210 / @rem2;
  display: flex;
  align-items: stretch;
}
.pie-center {
  position: absolute;
  left: 30%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  z-index: 1;
  div { font-size: 12 / @rem2; color: #9eb6dd; line-height: 1.3; }
  strong { display: block; margin-top: 2 / @rem2; font-size: 18 / @rem2; color: #fff; font-weight: 700; }
}
.map-panel {
  position: relative;
  display: flex;
  flex-direction: column;
}
.news {
  position: absolute;
  z-index: 3;
  left: 12 / @rem2;
  top: 12 / @rem2;
  width: 46%;
  max-width: 400 / @rem2;
  display: flex;
  flex-direction: column;
  padding: 8 / @rem2 10 / @rem2;
  .panel-tech();
  background: rgba(4, 18, 42, 0.82);
}
.news__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4 / @rem2;
  h4 {
    .panel-title-bar();
    margin-bottom: 0;
  }
}
.news__more {
  border: 0;
  background: transparent;
  color: @assistColor;
  font-size: 12 / @rem2;
  cursor: pointer;
  padding: 0;
  &:hover { color: #fff; }
}
.news__viewport {
  height: 104 / @rem2;
  overflow: hidden;
}
.news__swiper {
  width: 100%;
  height: 100%;
}
.news__viewport /deep/ .swiper-slide {
  height: 26 / @rem2 !important;
  box-sizing: border-box;
}
.news__item {
  display: flex;
  align-items: center;
  gap: 8 / @rem2;
  height: 100%;
  font-size: 12 / @rem2;
  color: #d7e6ff;
  cursor: pointer;
  .news__icon {
    flex-shrink: 0;
    font-size: 14 / @rem2;
    color: #5eb7ff;
    font-style: normal;
  }
  .c {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .t {
    flex-shrink: 0;
    color: #7ec8ff;
    white-space: nowrap;
  }
}
.news__empty {
  margin: 6 / @rem2 0 0;
  font-size: 12 / @rem2;
  color: #8096b8;
}
.map {
  flex: 1;
  min-height: 220 / @rem2;
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;
}
.branch-popup {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  pointer-events: none;
}
.branch-popup__panel {
  position: relative;
  pointer-events: auto;
  width: 360 / @rem2;
  max-width: 92%;
  padding: 18 / @rem2 20 / @rem2 16 / @rem2;
  border-radius: 8 / @rem2;
  background: linear-gradient(160deg, rgba(8, 42, 92, 0.96), rgba(4, 24, 58, 0.96));
  border: 1 / @rem2 solid rgba(0, 195, 255, 0.55);
  box-shadow: 0 8 / @rem2 28 / @rem2 rgba(0, 0, 0, 0.35);
  color: #e8f3ff;
}
.branch-popup__close {
  position: absolute;
  top: 6 / @rem2;
  right: 10 / @rem2;
  border: 0;
  background: transparent;
  color: #9ec9ef;
  font-size: 22 / @rem2;
  line-height: 1;
  cursor: pointer;
}
.branch-popup__title {
  margin: 0 28 / @rem2 12 / @rem2 0;
  font-size: 18 / @rem2;
  font-weight: 600;
  color: #00c3ff;
}
.branch-popup__kpis {
  display: flex;
  gap: 12 / @rem2;
  margin-bottom: 12 / @rem2;
  > div {
    flex: 1;
    padding: 8 / @rem2 10 / @rem2;
    border-radius: 6 / @rem2;
    background: rgba(0, 195, 255, 0.08);
    border: 1 / @rem2 solid rgba(0, 195, 255, 0.22);
    span {
      display: block;
      font-size: 12 / @rem2;
      color: #8eb0d4;
    }
    em {
      display: block;
      margin-top: 4 / @rem2;
      font-style: normal;
      font-size: 22 / @rem2;
      font-weight: 700;
      color: #fff;
    }
  }
}
.branch-popup__sub {
  margin: 0 0 8 / @rem2;
  font-size: 13 / @rem2;
  color: #9ec9ef;
}
.branch-popup__types {
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6 / @rem2 0;
    border-bottom: 1 / @rem2 solid rgba(0, 195, 255, 0.12);
    font-size: 13 / @rem2;
    &:last-child {
      border-bottom: 0;
    }
    span {
      color: #d7e6ff;
    }
    em {
      font-style: normal;
      color: #7dffc8;
      font-weight: 600;
      small {
        margin-left: 2 / @rem2;
        font-weight: 400;
        color: #8eb0d4;
      }
    }
  }
}
.honor {
  flex-shrink: 0;
  margin-top: 6 / @rem2;
  padding-top: 8 / @rem2;
  border-top: 1 / @rem2 solid fade(@mainColor, 20%);
}
.honor__head {
  display: flex;
  align-items: center;
  margin-bottom: 6 / @rem2;
  h4 {
    .panel-title-bar();
    margin-bottom: 0;
  }
}
.honor__empty {
  color: fade(#fff, 45%);
  font-size: 12 / @rem2;
  padding: 12 / @rem2 0;
  text-align: center;
}
.honor__viewport {
  overflow: hidden;
  width: 100%;
}
.honor__track {
  display: flex;
  will-change: transform;
}
.honor__card {
  flex: 0 0 25%;
  box-sizing: border-box;
  min-width: 0;
  padding: 0 5 / @rem2;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #cfe3ff;
  font-size: 11 / @rem2;
}
.honor__ph {
  height: 64 / @rem2;
  border-radius: 6 / @rem2;
  border: 1 / @rem2 solid fade(@mainColor, 28%);
  background-color: #0a2148;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  box-shadow: 0 4 / @rem2 12 / @rem2 rgba(0, 20, 50, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.honor__card:hover .honor__ph {
  transform: translateY(-2 / @rem2);
  box-shadow: 0 8 / @rem2 16 / @rem2 rgba(0, 80, 160, 0.35);
}
.honor__cap {
  margin-top: 4 / @rem2;
  min-width: 0;
  text-align: center;
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.lightbox {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 10, 24, 0.88);
  padding: 24 / @rem2;
}
.lightbox__stage {
  max-width: 920px;
  width: 78vw;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.lightbox__img {
  max-width: 100%;
  max-height: 72vh;
  object-fit: contain;
  border-radius: 8 / @rem2;
  box-shadow: 0 12 / @rem2 40 / @rem2 rgba(0, 0, 0, 0.45);
  background: #0a1a33;
}
.lightbox__meta {
  margin-top: 12 / @rem2;
  display: flex;
  align-items: center;
  gap: 10 / @rem2;
  color: #d7e6ff;
  font-size: 14 / @rem2;
  em {
    font-style: normal;
    padding: 2 / @rem2 8 / @rem2;
    border-radius: 10 / @rem2;
    background: rgba(64, 158, 255, 0.2);
    color: #7ec8ff;
  }
  strong { font-weight: 600; }
  span { color: #8aa6c8; }
}
.lightbox__close {
  position: absolute;
  top: 18 / @rem2;
  right: 24 / @rem2;
  width: 40 / @rem2;
  height: 40 / @rem2;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 28 / @rem2;
  line-height: 1;
  cursor: pointer;
}
.lightbox__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48 / @rem2;
  height: 48 / @rem2;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 32 / @rem2;
  line-height: 1;
  cursor: pointer;
  &--prev { left: 24 / @rem2; }
  &--next { right: 24 / @rem2; }
  &:hover { background: rgba(255, 255, 255, 0.22); }
}
.vol {
  flex: 0 0 auto;
}
.vol-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8 / @rem2;
  margin-top: 4 / @rem2;
  div {
    text-align: center;
    padding: 10 / @rem2 4 / @rem2;
    border-radius: 4 / @rem2;
    background:
      radial-gradient(ellipse at 50% 30%, rgba(55, 200, 150, 0.22), transparent 70%),
      rgba(8, 48, 40, 0.45);
    border: 1 / @rem2 solid fade(#36d7b7, 45%);
  }
  i {
    display: block;
    font-size: 20 / @rem2;
    color: #7dffc8;
    margin-bottom: 2 / @rem2;
  }
  span {
    display: block;
    font-size: 12 / @rem2;
    color: #b8dccf;
    margin-bottom: 2 / @rem2;
  }
  em {
    display: block;
    font-style: normal;
    font-size: 20 / @rem2;
    font-weight: 700;
    color: #fff;
    small {
      font-size: 12 / @rem2;
      font-weight: 400;
      color: #b8dccf;
    }
  }
}
.brand {
  position: relative;
  overflow: hidden;
  min-height: 140 / @rem2;
  flex: 0 0 auto;
  padding: 0;
}
.brand__cover {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-color: transparent;
  &.has-image::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(4, 16, 40, 0.82) 0%, rgba(4, 16, 40, 0.35) 55%, rgba(4, 16, 40, 0.15) 100%);
  }
}
.brand__more {
  position: absolute;
  right: 12 / @rem2;
  top: 10 / @rem2;
  z-index: 2;
  border: 0;
  background: transparent;
  color: #cfe3ff;
  font-size: 12 / @rem2;
  cursor: pointer;
}
.brand__body {
  position: relative;
  z-index: 1;
  padding: 12 / @rem2 14 / @rem2 14 / @rem2;
  h3 {
    margin: 0 0 6 / @rem2;
    padding-left: 0;
    color: #fff;
    font-size: 17 / @rem2;
    &::before { display: none; }
  }
}
.brand__label {
  margin: 0;
  font-size: 13 / @rem2;
  color: #d7e6ff;
}
.brand__num {
  margin: 2 / @rem2 0 10 / @rem2;
  b {
    font-size: 36 / @rem2;
    font-weight: 700;
    color: #fff;
    line-height: 1;
  }
  span {
    margin-left: 4 / @rem2;
    font-size: 13 / @rem2;
    color: #cfe3ff;
  }
}
.brand__btn {
  border: 1 / @rem2 solid fade(@mainColor, 75%);
  border-radius: 999 / @rem2;
  padding: 5 / @rem2 16 / @rem2;
  background: rgba(8, 40, 90, 0.45);
  color: #fff;
  font-size: 13 / @rem2;
  cursor: pointer;
  &:hover { background: rgba(0, 120, 200, 0.45); }
  &:active { transform: scale(0.96); }
}
</style>
